<script lang="ts">
	import { format } from 'svelte-i18n';
	import { onMount } from 'svelte';

	import type { Home } from 'src/types/Home';

	import {
		acceptUnitTransfer,
		cancelUnitTransfer,
		declineUnitTransfer,
	} from 'src/services/iotu/house-management';
	import { switchUnit } from 'src/services/auth_v2';
	import { homes, refreshHomes } from 'src/stores/account/home';
	import {
		pendingUnitTransfers,
		refreshPendingUnitTransfers,
	} from 'src/stores/account/pending_unit_transfers';
	import { advancedCardComponent } from 'src/stores/advanced_card';
	import { user_data } from 'src/stores';
	import { MAX_OWNED_HOMES } from 'src/constants';
	import { canAddOwnedHome } from 'src/helpers/houses/homes_helpers';

	import AcceptUnitTransferConsentModal from 'src/components/Settings/HomeManagement/AcceptUnitTransferConsentModal.svelte';
	import TransitionWrapper from 'src/components/Utility/TransitionWrapper.svelte';

	import { ActionsContainer, Alert, CircleAvatar, IconButton, Text, toast } from '@Shelly/shelly-design-system';

	let busyUnitId: number | null = null;
	let showAcceptConsentModal = false;
	let acceptUnitId: number | null = null;
	let acceptHomeName = '';

	$: outgoing = $pendingUnitTransfers.outgoing;
	$: incoming = $pendingUnitTransfers.incoming;
	$: hasPending = outgoing.length > 0 || incoming.length > 0;
	/** Accepting adds one owned home — blocked when already at the owned-home limit. */
	$: canAcceptIncoming = canAddOwnedHome($user_data.available_units);

	const findHome = (unitId: number): Home | undefined =>
		$homes.list.find((home) => String(home.id) === String(unitId));

	const resolveHomeName = (unitId: number): string => {
		const home = findHome(unitId);
		const transfer = incoming.find((item) => item.unit_id === unitId);
		return (
			home?.name || transfer?.name || $format('settings_default_home_name', { default: 'My Home' })
		);
	};

	async function handleCancel(unitId: number): Promise<void> {
		await runAction(
			unitId,
			cancelUnitTransfer,
			'error_cancel_unit_transfer',
			'Failed to cancel the transfer',
		);
	}

	async function handleDecline(unitId: number): Promise<void> {
		await runAction(
			unitId,
			declineUnitTransfer,
			'error_decline_unit_transfer',
			'Failed to decline the transfer',
		);
	}

	function openAcceptConsent(unitId: number): void {
		if (busyUnitId != null || !canAcceptIncoming) return;

		acceptUnitId = unitId;
		acceptHomeName = resolveHomeName(unitId);
		showAcceptConsentModal = true;
	}

	/**
	 * Accept finalizes ownership on the backend, then switch into the home so
	 * it becomes a normal owned unit in this session (same as add-home).
	 */
	async function handleAccept(): Promise<void> {
		if (busyUnitId != null || acceptUnitId == null) return;

		if (!canAddOwnedHome($user_data.available_units)) {
			toast.open({
				type: 'warning',
				title: $format('error_accept_unit_transfer_homes_limit', {
					values: { limit: MAX_OWNED_HOMES },
					default: `You already have ${MAX_OWNED_HOMES} homes. Delete one of your homes before accepting this transfer.`,
				}),
				duration: 4000,
			});
			showAcceptConsentModal = false;
			return;
		}

		const unitId = acceptUnitId;
		busyUnitId = unitId;
		showAcceptConsentModal = false;
		acceptUnitId = null;

		try {
			const result = await acceptUnitTransfer(unitId);
			if (!result.isok) {
				toast.open({
					type: 'danger',
					title: result?.errors?.transfer_expired
						? $format('error_unit_transfer_invite_expired', {
								default: 'The invitation to take ownership was declined or has expired.',
							})
						: $format('error_accept_unit_transfer', {
								default: 'Failed to accept the transfer',
							}),
				});
				await Promise.all([refreshHomes(), refreshPendingUnitTransfers()]);
				return;
			}

			// switchUnit refreshes homes on success; if switch fails, ownership still moved
			await switchUnit(String(unitId)).catch(() => refreshHomes());
			await refreshPendingUnitTransfers();

			toast.open({
				type: 'success',
				title: $format('success_accept_unit_transfer', {
					default: 'Home transfer accepted. The home is now yours.',
				}),
			});
		} catch {
			toast.open({
				type: 'danger',
				title: $format('error_accept_unit_transfer', {
					default: 'Failed to accept the transfer',
				}),
			});
		} finally {
			busyUnitId = null;
		}
	}

	async function runAction(
		unitId: number,
		action: (id: number) => Promise<{ isok: boolean; [key: string]: any }>,
		errorKey: string,
		errorDefault: string,
	): Promise<void> {
		if (busyUnitId != null) return;

		busyUnitId = unitId;
		try {
			const result = await action(unitId);
			await Promise.all([refreshHomes(), refreshPendingUnitTransfers()]);
			if (!result.isok) {
				toast.open({
					type: 'danger',
					title: result?.errors?.transfer_expired
						? $format('error_unit_transfer_invite_expired', {
								default: 'The invitation to take ownership was declined or has expired.',
							})
						: $format(errorKey, { default: errorDefault }),
				});
				return;
			}
		} catch {
			toast.open({
				type: 'danger',
				title: $format(errorKey, { default: errorDefault }),
			});
		} finally {
			busyUnitId = null;
		}
	}

	onMount(() => {
		void refreshPendingUnitTransfers();
	});
</script>

{#if hasPending}
	<Text styleType="body" class="mb-s mt-m">
		{$format('settings_pending_homes_group', { default: 'Pending transfers' })}
	</Text>

	{#if outgoing.length > 0}
		{#each outgoing as transfer (transfer.unit_id)}
			{@const home = findHome(transfer.unit_id)}
			<TransitionWrapper>
				<div class="mb-s pending-home-card">
					<svelte:component
						this={$advancedCardComponent}
						title={home?.name ?? transfer.name}
						subtitle={{
							value: $format('settings_transfer_pending_to', {
								default: 'Pending transfer to {email}',
								values: { email: transfer.to_email ?? '' },
							}),
							type: 'muted',
							bold: true,
							textSmall: false,
						}}
					>
						<CircleAvatar
							slot="left"
							class="pt-xs pb-xs pl-s"
							size="m"
							image={home?.avatar?.image}
							backgroundColor={home?.avatar?.backgroundColor ?? 'color-purple'}
						/>
						<ActionsContainer type="simple" slot="right">
							<IconButton
								slot="first"
								icon="fas fa-times"
								variant="help-2"
								disabled={busyUnitId === transfer.unit_id}
								aria-label={$format('button_cancel', { default: 'Cancel' })}
								on:click={() => handleCancel(transfer.unit_id)}
							/>
						</ActionsContainer>
					</svelte:component>
				</div>
			</TransitionWrapper>
		{/each}
	{/if}

	{#if incoming.length > 0}
		{#if !canAcceptIncoming}
			<Alert
				type="warning"
				class="mb-s"
				heading={$format('error_accept_unit_transfer_homes_limit', {
					values: { limit: MAX_OWNED_HOMES },
					default: `You already have ${MAX_OWNED_HOMES} homes. Delete one of your homes before accepting this transfer.`,
				})}
			/>
		{/if}

		{#each incoming as transfer (transfer.unit_id)}
			{@const home = findHome(transfer.unit_id)}
			<TransitionWrapper>
				<div class="mb-s pending-home-card">
					<svelte:component
						this={$advancedCardComponent}
						title={home?.name ?? transfer.name}
						subtitle={{
							value: $format('settings_transfer_pending_from', {
								default: 'Pending transfer from {email}',
								values: { email: transfer.from_email ?? '' },
							}),
							type: 'muted',
							bold: true,
							textSmall: false,
						}}
					>
						<CircleAvatar
							slot="left"
							class="pt-xs pb-xs pl-s"
							size="m"
							image={home?.avatar?.image}
							backgroundColor={home?.avatar?.backgroundColor ?? 'color-purple'}
						/>
						<ActionsContainer type="simple" slot="right">
							<IconButton
								slot="first"
								icon="fas fa-times"
								variant="help-2"
								disabled={busyUnitId === transfer.unit_id}
								aria-label={$format('button_decline_transfer', { default: 'Decline' })}
								on:click={() => handleDecline(transfer.unit_id)}
							/>
							<IconButton
								slot="second"
								icon="fas fa-check"
								variant="help-2"
								disabled={busyUnitId === transfer.unit_id || !canAcceptIncoming}
								aria-label={$format('button_accept', { default: 'Accept' })}
								on:click={() => openAcceptConsent(transfer.unit_id)}
							/>
						</ActionsContainer>
					</svelte:component>
				</div>
			</TransitionWrapper>
		{/each}
	{/if}
{/if}

<AcceptUnitTransferConsentModal
	bind:showModal={showAcceptConsentModal}
	homeName={acceptHomeName}
	loading={busyUnitId === acceptUnitId && acceptUnitId != null}
	on:confirm={handleAccept}
/>

<style>
	.pending-home-card :global(.advanced-card) {
		overflow: visible;
	}
</style>
